// A channel's or space's settings window (18-channel-settings.md): General (profile, access,
// message history, encryption), Members (Joined, Invited and Banned; Invite) and Permissions, each row a
// choice of the fixed roles, changes collected and applied together. The Bans page, the sort
// chip, the Left and Removed filters, the ask-to-join rules, history for guests and the catch-all
// rows were candidates to drop.
import { EventType, HistoryVisibility, JoinRule, type MatrixClient, type Room, type RoomMember } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { circleName } from '../accounts/accounts';
import { noAutofill } from '../blocks/Field';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Confirm } from '../blocks/Dialog';
import { Skeletons, SLOW_MS } from '../blocks/Wait';
import { describe } from '../core/errors';
import { compareMatches, matches } from '../core/filter';
import { t, type Key } from '../i18n';
import { thumbnail } from '../session/media';
import { parentsOf, useRoomState } from '../session/rooms';
import { chatIcons } from '../chat/icons';
import { frameIcons } from '../frame/icons';
import { Icon, icons } from '../blocks/icons';
import { Plain } from '../chat/html';
import { showPerson } from '../people/PersonCard';
import { inviteTo, mayInvite } from '../people/pages';
import { Card, Choice, Section, SettingsWindow } from './frame';
import { founders, levels, powerOf, roleFor, roleName, ROLES, type PowerLevels } from './roles';
import { DeleteSection } from './DeleteRoom';
import { ProfileCard, type Picture } from './ProfileCard';
import { BannerSection } from '../frame/Banner';
import { nameIn } from '../chat/events';
import { localPart, personPicture } from '../people/names';

type Page = 'general' | 'members' | 'permissions';
let openWindow: ((p: { room: Room; page?: Page } | undefined) => void) | undefined;
/** Opens a room's settings (from its menus, a lobby item, the person card's "Manage permissions"). */
export const openRoomSettings = (room: Room, page?: Page) => openWindow?.({ room, page });
export const closeRoomSettings = () => openWindow?.(undefined);

export function RoomSettingsHost({ client }: { client: MatrixClient }) {
  const [open, setOpen] = useState<{ room: Room; page?: Page }>();
  useEffect(() => {
    openWindow = setOpen;
    return () => void (openWindow = undefined);
  }, []);
  return open ? <RoomSettings client={client} room={open.room} start={open.page} onClose={() => setOpen(undefined)} /> : null;
}

function RoomSettings({ client, room, start, onClose }: { client: MatrixClient; room: Room; start?: Page; onClose: () => void }) {
  useRoomState(room);
  const phone = matchMedia('(max-width: 750px)').matches;
  const [page, setPage] = useState<string | undefined>(start ?? (phone ? undefined : 'general'));
  useEffect(() => {
    if (room.getMyMembership() !== 'join') onClose();
  });
  return (
    <SettingsWindow
      fromRoom
      label={room.name}
      top={
        <div className="settings-room">
          <Avatar id={room.roomId} name={room.name} src={thumbnail(client, room.getMxcAvatarUrl(), 24)} size={24} round={false} />
          <strong>{room.name}</strong>
        </div>
      }
      groups={[
        { entries: [{ key: 'general', label: t('settings.general'), icon: frameIcons.settings }] },
        {
          heading: t('settings.userManagement'),
          entries: [
            { key: 'members', label: t('settings.members'), icon: chatIcons.person },
            { key: 'permissions', label: t('settings.permissions'), icon: icons.lock },
          ],
        },
      ]}
      page={page}
      onPage={setPage}
      onClose={onClose}
      render={(p) => (p === 'members' ? <Members client={client} room={room} /> : p === 'permissions' ? <Permissions client={client} room={room} /> : <General client={client} room={room} />)}
    />
  );
}

const may = (client: MatrixClient, room: Room, type: string) => room.currentState.maySendStateEvent(type, client.getUserId()!);

function General({ client, room }: { client: MatrixClient; room: Room }) {
  const space = room.isSpaceRoom();
  const state = room.currentState;
  const topic: string = state.getStateEvents(EventType.RoomTopic, '')?.getContent().topic ?? '';
  const [about, setAbout] = useState(topic);
  const [busy, setBusy] = useState<string>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [encrypting, setEncrypting] = useState(false);

  const save = async (name: string, picture: Picture) => {
    if (picture.file) {
      const { content_uri } = await client.uploadContent(picture.file, { type: picture.file.type });
      await client.sendStateEvent(room.roomId, EventType.RoomAvatar, { url: content_uri } as never, '');
    } else if (picture.remove) await client.sendStateEvent(room.roomId, EventType.RoomAvatar, {} as never, '');
    if (name !== room.name) await client.setRoomName(room.roomId, name);
    if (about.trim() !== topic) await client.setRoomTopic(room.roomId, about.trim());
  };
  const send = async (key: string, type: string, content: object) => {
    setBusy(key);
    setErrors((e) => ({ ...e, [key]: '' }));
    await client.sendStateEvent(room.roomId, type as never, content as never, '').catch((e) => setErrors((x) => ({ ...x, [key]: describe(e, 'save').text })));
    setBusy(undefined);
  };

  // Access: the choices the room version allows (18, Access).
  const version = Number(room.getVersion()) || 0;
  const parents = parentsOf(client, room.roomId);
  const rule = room.getJoinRule();
  // A space made with "Everyone in <Circle>" (public, never federated) says so in place of "Anyone".
  const circle = space && state.getStateEvents(EventType.RoomCreate, '')?.getContent()['m.federate'] === false;
  const access = rule === JoinRule.Public ? 'public' : rule === JoinRule.Restricted ? 'restricted' : rule === JoinRule.Invite || rule === JoinRule.Private ? 'invite' : 'other';
  const accessOptions = [
    { value: 'invite', label: t('settings.inviteOnly') },
    ...(version >= 8 && parents.length ? [{ value: 'restricted', label: t('settings.spaceMembers') }] : []),
    { value: 'public', label: circle ? t('create.everyone', { circle: circleName(client.getUserId()!) }) : t('settings.anyone') },
    ...(access === 'other' ? [{ value: 'other', label: t('settings.other') }] : []),
  ];
  const history = state.getStateEvents(EventType.RoomHistoryVisibility, '')?.getContent().history_visibility ?? HistoryVisibility.Shared;
  const encrypted = room.hasEncryptionStateEvent();

  return (
    <>
      <ProfileCard
        id={room.roomId}
        name={room.name}
        text={topic && <Plain text={topic} />}
        src={thumbnail(client, room.getMxcAvatarUrl(), 48)}
        may={{ edit: [EventType.RoomAvatar, EventType.RoomName, EventType.RoomTopic].some((type) => may(client, room, type)), picture: may(client, room, EventType.RoomAvatar), name: may(client, room, EventType.RoomName) }}
        fields={
          <div className="field">
            <textarea id="settings-topic" placeholder=" " value={about} readOnly={!may(client, room, EventType.RoomTopic)} aria-label={t('create.topic')} onChange={(e) => setAbout(e.target.value)} />
            <label htmlFor="settings-topic">{t('create.topic')}</label>
          </div>
        }
        onCancel={() => setAbout(topic)}
        onSave={save}
      />
      <BannerSection client={client} room={room} />
      <Section title={t('create.options')}>
        <Card
          icon={chatIcons.login}
          title={t(space ? 'settings.spaceAccess' : 'settings.channelAccess')}
          text={t(space ? 'settings.spaceAccessLine' : 'settings.channelAccessLine')}
          error={errors.access}
          control={
            <Choice
              label={t(space ? 'settings.spaceAccess' : 'settings.channelAccess')}
              value={access}
              options={accessOptions}
              disabled={!may(client, room, EventType.RoomJoinRules)}
              busy={busy === 'access'}
              onChange={(v) =>
                void send('access', EventType.RoomJoinRules, v === 'restricted' ? { join_rule: JoinRule.Restricted, allow: parents.map((p) => ({ type: 'm.room_membership', room_id: p.roomId })) } : { join_rule: v })
              }
            />
          }
        />
        {!space && (
          <Card
            icon={chatIcons.visibility}
            title={t('settings.history')}
            text={t('settings.historyLine')}
            error={errors.history}
            control={
              <Choice
                label={t('settings.history')}
                value={history}
                options={[
                  { value: HistoryVisibility.Shared, label: t('settings.historyAll') },
                  { value: HistoryVisibility.Invited, label: t('settings.historyInvited') },
                  { value: HistoryVisibility.Joined, label: t('settings.historyJoined') },
                ]}
                disabled={!may(client, room, EventType.RoomHistoryVisibility)}
                busy={busy === 'history'}
                onChange={(v) => void send('history', EventType.RoomHistoryVisibility, { history_visibility: v })}
              />
            }
          />
        )}
        {!space && (
          <Card
            icon={chatIcons.keep}
            title={t('settings.encryption')}
            text={t(encrypted ? 'settings.encryptedLine' : 'settings.notEncryptedLine')}
            error={errors.encryption}
            control={
              encrypted ? (
                <span className="badge-on">{t('settings.on')}</span>
              ) : (
                <Button small disabled={!may(client, room, EventType.RoomEncryption)} busy={busy === 'encryption'} onClick={() => setEncrypting(true)}>
                  {t('settings.turnOn')}
                </Button>
              )
            }
          />
        )}
      </Section>
      {encrypting && (
        <Confirm
          title={t('settings.turnOnTitle')}
          text={t('settings.turnOnText')}
          action={t('settings.turnOn')}
          onConfirm={() => client.sendStateEvent(room.roomId, EventType.RoomEncryption, { algorithm: 'm.megolm.v1.aes-sha2' } as never, '')}
          onClose={() => setEncrypting(false)}
        />
      )}
      <DeleteSection client={client} room={room} />
    </>
  );
}

function Members({ client, room }: { client: MatrixClient; room: Room }) {
  const [filter, setFilter] = useState<'join' | 'invite' | 'ban'>('join');
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    void room.loadMembersIfNeeded().catch(() => {});
    const timer = setTimeout(() => setSlow(true), SLOW_MS);
    return () => clearTimeout(timer);
  }, [room]);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed), 200);
    return () => clearTimeout(timer);
  }, [typed]);
  const label = (m: RoomMember) => `${nameIn(room, m.userId)} ${/^@|:/.test(query) ? m.userId : localPart(m.userId)}`;
  const people = room.getMembersWithMembership(filter);
  const shown = query.trim() ? people.filter((m) => matches(label(m), query, true)).sort((a, b) => compareMatches(label(a), label(b), query)) : [...people].sort((a, b) => a.name.localeCompare(b.name));
  const byRole = new Map<number, RoomMember[]>();
  shown.forEach((m) => byRole.set(powerOf(room, m.userId), [...(byRole.get(powerOf(room, m.userId)) ?? []), m]));
  const empty: Record<string, Key> = { join: 'settings.noMembers', invite: 'settings.noInvited', ban: 'settings.noBanned' };
  const missing = !slow && filter === 'join' ? room.getJoinedMemberCount() - people.length : 0;
  return (
    <div className="settings-members" aria-busy={missing > 0 || undefined}>
      <div className="settings-count">
        <h3>{t('settings.membersCount', { n: room.getJoinedMemberCount() })}</h3>
        <Button small disabled={!mayInvite(client, room)} onClick={() => inviteTo(room)}>
          <Icon svg={chatIcons.personAdd} size={18} />
          {t('people.invite')}
        </Button>
      </div>
      <div className="settings-search">
        <label className="picker-field">
          <input {...noAutofill} value={typed} placeholder={t('settings.searchMembers')} aria-label={t('settings.searchMembers')} onChange={(e) => setTyped(e.target.value)} />
          {query.trim() && (
            <button type="button" className={shown.length ? 'chip found' : 'chip none'} onClick={() => setTyped('')}>
              {shown.length ? t('settings.results', { n: shown.length }) : t('emoji.noResults')}
            </button>
          )}
        </label>
        <Choice
          label={{ join: t('settings.joined'), invite: t('people.invited'), ban: t('people.banned') }[filter]}
          value={filter}
          options={[
            { value: 'join', label: t('settings.joined') },
            { value: 'invite', label: t('people.invited') },
            { value: 'ban', label: t('people.banned') },
          ]}
          onChange={setFilter}
        />
      </div>
      {!shown.length && !query.trim() && missing <= 0 && <p className="picker-note">{t(empty[filter])}</p>}
      {[...byRole.entries()]
        .sort((a, b) => b[0] - a[0])
        .map(([power, list]) => (
          <section key={power}>
            <h4 className="member-heading">{roleName(power)}</h4>
            {list.map((m) => (
              <button key={m.userId} type="button" className="picker-row" data-desyr-user={m.userId} onClick={(e) => showPerson(m.userId, e.currentTarget, room)}>
                <Avatar id={m.userId} name={nameIn(room, m.userId)} src={thumbnail(client, personPicture(client, m.userId, room), 32)} size={32} round={false} />
                <span>
                  <span className="picker-name">{nameIn(room, m.userId)}</span>
                  <span className="picker-second">{localPart(m.userId)}</span>
                </span>
              </button>
            ))}
          </section>
        ))}
      {/* Skeleton rows under the known members, not a spinner (02, Loading, v1.58.24). */}
      {missing > 0 && <Skeletons count={missing} bars={2} className="picker-row" />}
    </div>
  );
}

/** The permission rows (18, "Desyr's permission rows"): where each one's power is kept, and its default. */
interface Row {
  key: Key;
  get: (l: PowerLevels) => number | undefined;
  set: (l: PowerLevels, v: number) => void;
  fallback: (l: PowerLevels) => number;
}
const message = (key: Key, type: string): Row => ({ key, get: (l) => l.events?.[type], set: (l, v) => (l.events = { ...l.events, [type]: v }), fallback: (l) => l.events_default ?? 0 });
const state = (key: Key, type: string): Row => ({ ...message(key, type), fallback: (l) => l.state_default ?? 50 });
const action = (key: Key, f: 'invite' | 'kick' | 'ban' | 'redact', d: number): Row => ({ key, get: (l) => l[f], set: (l, v) => (l[f] = v), fallback: () => d });
const roomMention: Row = { key: 'settings.mentionRoom', get: (l) => l.notifications?.room, set: (l, v) => (l.notifications = { ...l.notifications, room: v }), fallback: () => 50 };
const moderation = [action('people.invite', 'invite', 0), action('settings.removePeople', 'kick', 50), action('people.ban', 'ban', 50)];
const packs = state('settings.emojiPacks', 'im.ponies.room_emotes');

const CHANNEL_ROWS: [Key, Row[]][] = [
  ['settings.groupMessages', [message('settings.sendMessages', EventType.RoomMessage), message('settings.sendStickers', EventType.Sticker), message('settings.sendReactions', EventType.Reaction), roomMention, state('settings.pinMessages', EventType.RoomPinnedEvents)]],
  ['settings.groupCalls', [state('settings.calls', 'org.matrix.msc3401.call.member')]],
  ['settings.groupModeration', [...moderation, action('settings.deleteOthers', 'redact', 50), message('settings.deleteOwn', EventType.RoomRedaction)]],
  ['settings.groupOverview', [state('settings.picture', EventType.RoomAvatar), state('create.name', EventType.RoomName), state('create.topic', EventType.RoomTopic)]],
  ['settings.groupSettings', [state('settings.changeAccess', EventType.RoomJoinRules), state('settings.changePermissions', EventType.RoomPowerLevels), state('settings.turnOnEncryption', EventType.RoomEncryption), state('settings.history', EventType.RoomHistoryVisibility)]],
  ['settings.groupOther', [packs]],
];
const SPACE_ROWS: [Key, Row[]][] = [
  ['settings.groupManage', [state('settings.manageChannels', EventType.SpaceChild)]],
  ['settings.groupModeration', moderation],
  ['settings.groupSpaceOverview', [state('settings.spacePicture', EventType.RoomAvatar), state('settings.spaceName', EventType.RoomName), state('settings.spaceTopic', EventType.RoomTopic)]],
  ['settings.groupSettings', [state('settings.changeSpaceAccess', EventType.RoomJoinRules), state('settings.changePermissions', EventType.RoomPowerLevels)]],
  ['settings.groupOther', [packs]],
];

function Permissions({ client, room }: { client: MatrixClient; room: Room }) {
  const current = levels(room);
  const groups = room.isSpaceRoom() ? SPACE_ROWS : CHANNEL_ROWS;
  const valueOf = (row: Row) => row.get(current) ?? row.fallback(current);
  const [changes, setChanges] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const mayChange = may(client, room, EventType.RoomPowerLevels);
  const options = ROLES.map((p) => ({ value: p, label: p === 100 ? roleName(p) : t('settings.andAbove', { role: roleName(p) }) }));
  const apply = async () => {
    setBusy(true);
    setError(undefined);
    // The whole content: every row written out, then the changes.
    const next: PowerLevels = structuredClone(current);
    groups.forEach(([, rows]) => rows.forEach((row) => row.set(next, changes[row.key] ?? valueOf(row))));
    try {
      await client.sendStateEvent(room.roomId, EventType.RoomPowerLevels, next as never, '');
      setChanges({});
    } catch (e) {
      setError(t('settings.notApplied', { reason: describe(e, 'save').text }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {founders(room).size > 0 && <p className="settings-note">{t('settings.foundersLine')}</p>}
      {groups.map(([group, rows]) => (
        <Section key={group} title={t(group)}>
          {rows.map((row) => {
            const value = changes[row.key] ?? roleFor(valueOf(row));
            return (
              <Card
                key={row.key}
                title={t(row.key)}
                control={
                  <span className={row.key in changes ? 'changed' : undefined}>
                    <Choice
                      label={t(row.key)}
                      value={value}
                      options={options}
                      disabled={!mayChange}
                      onChange={(v) => setChanges(({ [row.key]: _, ...rest }) => (v === roleFor(valueOf(row)) ? rest : { ...rest, [row.key]: v }))}
                    />
                  </span>
                }
              />
            );
          })}
        </Section>
      ))}
      {(Object.keys(changes).length > 0 || error) && (
        <div className="settings-bar" role="status">
          <span>{error ?? t('settings.changes')}</span>
          <Button small variant="text" onClick={() => (setChanges({}), setError(undefined))}>
            {t('settings.reset')}
          </Button>
          <Button small variant="filled" busy={busy} onClick={() => void apply()}>
            {t('settings.apply')}
          </Button>
        </div>
      )}
    </>
  );
}
